<template>
  <div v-if="open" class="fixed inset-0 z-50 flex items-center justify-center">
    <div class="absolute inset-0 bg-black/50" @click="$emit('close')"></div>

    <div class="relative bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 p-4">
      <div class="flex items-center justify-between mb-3">
        <h3 class="text-lg font-semibold">Generate Video Impression Report</h3>
        <button class="px-2 py-1 text-gray-500 hover:text-gray-700" @click="$emit('close')">✕</button>
      </div>

      <div v-if="error" class="text-red-600 text-sm mb-2">{{ error }}</div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label class="block text-xs text-gray-600 mb-1">Campaign UUID</label>
          <input class="w-full border rounded px-2 py-1 bg-gray-100" :value="report?.campaign_uuid" disabled />
        </div>

        <div>
          <label class="block text-xs text-gray-600 mb-1">Creative</label>
          <select class="w-full border rounded px-2 py-1" v-model="form.creative_uuid">
            <option value="" disabled>Select creative…</option>
            <option v-for="c in creatives" :key="c.uuid" :value="c.uuid">
              {{ c.user_defined_name || c.uuid }}
            </option>
          </select>
          <div v-if="creativeError" class="text-xs text-red-500 mt-1">{{ creativeError }}</div>
        </div>

        <div>
          <label class="block text-xs text-gray-600 mb-1">Country (optional)</label>
          <select class="w-full border rounded px-2 py-1" v-model="form.country_uuid">
            <option :value="null">— none / all —</option>
            <option v-for="c in countries" :key="c.uuid || c.name" :value="c.uuid">
              {{ c.name }}
            </option>
          </select>
          <div v-if="countryError" class="text-xs text-red-500 mt-1">{{ countryError }}</div>
        </div>

        <div class="grid grid-cols-2 gap-2">
          <div>
            <label class="block text-xs text-gray-600 mb-1">Start Date</label>
            <input type="date" class="w-full border rounded px-2 py-1" v-model="form.start_date" />
          </div>
          <div>
            <label class="block text-xs text-gray-600 mb-1">End Date</label>
            <input type="date" class="w-full border rounded px-2 py-1" v-model="form.end_date" />
          </div>
        </div>
      </div>

      <div class="mt-4 flex gap-2 justify-end">
        <button class="px-3 py-1 bg-gray-200 rounded" @click="$emit('close')">Cancel</button>
        <button
          class="px-3 py-1 bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50"
          :disabled="!form.creative_uuid || busy"
          @click="submit"
        >
          {{ busy ? 'Queuing…' : 'Queue Report' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
/* eslint-env vue/setup-compiler-macros */
import { reactive, watch } from 'vue'

const props = defineProps({
  open: { type: Boolean, default: false },
  report: { type: Object, default: null },
  creatives: { type: Array, default: () => [] },
  countries: { type: Array, default: () => [] },
  creativeError: { type: String, default: '' },
  countryError: { type: String, default: '' },
  prefillCountryUuid: { type: String, default: null },
  busy: { type: Boolean, default: false },
  error: { type: String, default: '' }
})

const emit = defineEmits(['close', 'submit'])

const form = reactive({
  creative_uuid: '',
  country_uuid: null,
  start_date: '',
  end_date: ''
})

const toDateStr = (v) => (typeof v === 'string' ? v.slice(0, 10) : '')
const resolveStart = (r) =>
  toDateStr(r?.date_range?.start) ||
  toDateStr(r?.overall_start_date) ||
  toDateStr(r?.start_date) ||
  toDateStr(r?.campaign?.start_date) ||
  ''
const resolveEnd = (r) =>
  toDateStr(r?.date_range?.end) ||
  toDateStr(r?.overall_end_date) ||
  toDateStr(r?.end_date) ||
  toDateStr(r?.campaign?.end_date) ||
  ''

// Prefill on open
watch(
  () => props.open,
  (v) => {
    if (!v) return
    form.creative_uuid = props.creatives?.[0]?.uuid || ''
    form.country_uuid = props.prefillCountryUuid ?? null
    form.start_date = resolveStart(props.report)
    form.end_date = resolveEnd(props.report)
  },
  { immediate: true }
)

// If data arrives after open, set sensible defaults once
watch(
  () => props.creatives,
  (list) => {
    if (!props.open) return
    if (!form.creative_uuid) form.creative_uuid = list?.[0]?.uuid || ''
  },
  { deep: true }
)

watch(
  () => props.countries,
  () => {
    if (!props.open) return
    if (form.country_uuid == null && props.prefillCountryUuid)
      form.country_uuid = props.prefillCountryUuid
  },
  { deep: true }
)

const submit = () => {
  if (!props.report?.id) return
  if (!form.creative_uuid) return
  if (form.start_date && form.end_date && form.start_date > form.end_date) return

  emit('submit', {
    native_display_report_id: props.report.id,
    campaign_uuid: props.report.campaign_uuid,
    creative_uuid: form.creative_uuid,
    country_uuid: form.country_uuid || null,
    start_date: form.start_date || null,
    end_date: form.end_date || null
  })
}
</script>